// @file Tiptap extension that signifies the visibility and position of a contextual floating toolbar that appears near a text selection
// Copied from packages/universal-post-editor/src/tiptap/extension-floating-toolbar.ts
// All the code is the same except for the plugin name `whiteboardFloatingToolbar`
import type { Editor, EditorState, EditorView, PluginView } from '@padlet/whiteboard-tiptap'
import { Extension, isNodeSelection, isTextSelection, Plugin, PluginKey, posToDOMRect } from '@padlet/whiteboard-tiptap'

interface FloatingToolbarOptions {
  /**
   * Function that returns the floating toolbar element.
   */
  getElement: () => HTMLElement | undefined
  /**
   * Callback to show the floating toolbar.
   */
  onShow?: ({ editor, rect }: { editor: Editor; rect: DOMRect }) => void
  /**
   * Callback to hide the floating toolbar.
   */
  onHide?: () => void
}

interface FloatingToolbarPluginOptions extends FloatingToolbarOptions {
  editor: Editor
}

// Same as services/rails/app/javascript/bits/tiptap_helper.ts
// The only difference is that isTextSelection is imported from @padlet/whiteboard-tiptap
const isBlankTextSelection = (editor: Editor): boolean => {
  const { view, state } = editor
  const selection = view.state.selection
  const { from, to } = selection
  return state.doc.textBetween(from, to).trim().length === 0 && isTextSelection(selection)
}

class FloatingToolbarView implements PluginView {
  readonly editor: Editor
  readonly getElement?: FloatingToolbarPluginOptions['getElement']
  readonly onShow?: FloatingToolbarPluginOptions['onShow']
  readonly onHide?: FloatingToolbarPluginOptions['onHide']
  isShowing = false
  mousedownRegistered = false
  mousedownRegisterTimer: number | undefined = undefined

  constructor({ editor, getElement, onShow, onHide }: FloatingToolbarPluginOptions) {
    this.editor = editor
    this.getElement = getElement
    this.onShow = onShow
    this.onHide = onHide

    this.editor.on('blur', this.blurHandler)
  }

  show = ({ view, from, to }: { view: EditorView; from: number; to: number }): void => {
    const rect = this.getSelectionRect({ view, from, to })
    if (rect == null) return
    this.isShowing = true
    this.onShow?.({ editor: this.editor, rect })
    this.tryAttachMousedownHandler()
  }

  hide = (): void => {
    this.isShowing = false
    this.onHide?.()
    this.detachMousedownHandler()
  }

  shouldShow = ({ view, state }: { view: EditorView; state: EditorState; prevState: EditorState }): boolean => {
    const { selection } = state
    const { empty } = selection

    // Sometime check for `empty` is not enough.
    // Doubleclick an empty paragraph returns a node size of 2.
    // So we check also for an empty text size.
    const isEmptyTextBlock = isBlankTextSelection(this.editor)

    // When clicking on a element inside the bubble menu the editor "blur" event
    // is called and the bubble menu item is focussed. In this case we should
    // consider the menu as part of the editor and keep showing the menu
    const isChildOfMenu = this.getElement?.()?.contains(document.activeElement) ?? false
    const hasEditorFocus = view.hasFocus() === true || isChildOfMenu

    return this.editor.isEditable && hasEditorFocus && empty === false && !isEmptyTextBlock
  }

  shouldUpdate = ({
    view,
    state,
    prevState,
  }: {
    view: EditorView
    state: EditorState
    prevState: EditorState
  }): boolean => {
    const { composing } = view
    const { selection } = state
    const hasSelectionChanged = prevState.selection.eq(selection) === false
    return composing === false && hasSelectionChanged
  }

  getSelectionRect = ({ view, from, to }: { view: EditorView; from: number; to: number }): DOMRect | undefined => {
    const { state } = view
    let rect: DOMRect | undefined
    if (isNodeSelection(state.selection)) {
      let node = view.nodeDOM(from) as HTMLElement
      const nodeViewWrapper =
        node.dataset.nodeViewWrapper != null ? node : node.querySelector('[data-node-view-wrapper]')
      if (nodeViewWrapper != null) {
        node = nodeViewWrapper.firstChild as HTMLElement
      }
      if (node != null) {
        rect = node.getBoundingClientRect()
      }
    } else {
      rect = posToDOMRect(view, from, to)
    }
    return rect
  }

  blurHandler = ({ event }: { event: FocusEvent }): void => {
    if (event.relatedTarget != null && this.getElement?.()?.contains(event.relatedTarget as Node) === true) {
      return
    }

    this.hide()
  }

  /*****
   * Having a menu that disappears when the input loses focus but stays visible when the menu is clicked is a
   * common pattern. However, this is usually made difficult by the event execution order -- the `blur` event
   * on the input often fires before the `click` event on the menu, causing it to disappear before the `click`
   * has the chance to fire. This makes interactive elements in the menu unusable.
   * ~~To solve this, we can use an event that fires before `blur`, such as `focusin` or `mousedown`, to set a
   * flag that prevents the `blur` event handler from hiding the menu. We choose `mousedown` because it's
   * fired more consistently in different browsers than `focusin`.~~
   * UPDATE (16/4/2024): We found a better solution -- just call `preventDefault` on the `mousedown` event.
   */

  mousedownHandler = (e: MouseEvent): void => {
    e.preventDefault()
  }

  /*****
   * The menu may take some time to render (that's why we accept a function that returns the element, not the
   * element itself). We can only attach the event handler once the element is available in the DOM. Because
   * it's so important in this case to make elements in the menu usable so we try attaching until we can
   * successfully do so.
   */

  tryAttachMousedownHandler = (): void => {
    if (!this.mousedownRegistered) {
      this.mousedownRegisterTimer = window.setTimeout(() => {
        const element = this.getElement?.()
        if (element != null) {
          element.addEventListener('mousedown', this.mousedownHandler, { capture: true })
          this.mousedownRegistered = true
        } else {
          this.tryAttachMousedownHandler()
        }
      }, 0)
    }
  }

  detachMousedownHandler = (): void => {
    this.getElement?.()?.removeEventListener('mousedown', this.mousedownHandler, { capture: true })
    this.mousedownRegistered = false
    window.clearTimeout(this.mousedownRegisterTimer)
    this.mousedownRegisterTimer = undefined
  }

  /**************************
   * ProseMirror plugin hooks
   **************************/

  update(view: EditorView, prevState: EditorState): void {
    const { state } = view
    const { ranges } = state.selection
    const from = Math.min(...ranges.map((range) => range.$from.pos))
    const to = Math.max(...ranges.map((range) => range.$to.pos))

    const hasValidSelection = state.selection.$from.pos !== state.selection.$to.pos
    if (!hasValidSelection && this.isShowing) {
      this.hide()
    } else if (!this.isShowing && this.shouldShow({ view, state, prevState })) {
      this.show({ view, from, to })
    } else if (this.isShowing && this.shouldUpdate({ view, state, prevState })) {
      this.show({ view, from, to })
    }
  }

  destroy = (): void => {
    this.hide()
    this.editor.off('blur', this.blurHandler)
  }
}

const FloatingToolbarPlugin = (options: FloatingToolbarPluginOptions): Plugin => {
  return new Plugin({
    key: new PluginKey('floatingToolbar'),
    view: () => new FloatingToolbarView(options),
  })
}

const FloatingToolbar = Extension.create<FloatingToolbarOptions>({
  name: 'whiteboardFloatingToolbar',

  addOptions() {
    return {
      getElement: () => undefined,
      onShow: undefined,
      onHide: undefined,
    }
  },

  addProseMirrorPlugins() {
    return [
      FloatingToolbarPlugin({
        editor: this.editor,
        getElement: this.options.getElement,
        onShow: this.options.onShow,
        onHide: this.options.onHide,
      }),
    ]
  },
})

export { FloatingToolbar }
